import React from "react";
import { useTranslation } from "react-i18next";
import { BookingLanguage, BookingStep } from "@/types/booking";
import { cn } from "@/lib/utils";
import { ProgressBar } from "@/components/ui/progress-bar";
import LanguageSelector from "./LanguageSelector";
import CountrySelector from "./CountrySelector";
import { aheadLogo } from "@/assets/images";
import SupportButton from "@/components/SupportButton";
import MemberHeaderBadge from "./MemberHeaderBadge";
import { languageMap } from "@/i18n";
import { useMembershipPlanStore } from "@/stores/membershipPlanStore";
import { useMarket } from "@/hooks/useMarket";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { getIndicatorSteps } from "@/stores/bookingFlowModel";
import { getHomepageUrl, marketHasMembership } from "@/config/market";

interface BookingHeaderProps {
  language: BookingLanguage;
  setLanguage?: (lang: BookingLanguage) => void;
  currentStep?: BookingStep;
  onStepClick?: (step: BookingStep) => void;
  onBack?: () => void;
  skeletonMode?: boolean;
}

const BookingHeader = ({
  language,
  setLanguage,
  currentStep,
  onStepClick,
  onBack,
  skeletonMode = false,
}: BookingHeaderProps) => {
  const { t: i18n } = useTranslation(undefined, { lng: languageMap[language] });

  const { market } = useMarket();
  const { data: featureFlags } = useFeatureFlags();
  // Key the extra cadence + cross-sell dots on the entry DIRECTION, not merely on
  // `active`: an MRI-first bundle sets `active` too but never visits those steps,
  // so keying on `active` would jump the count to "4 of 7". Mirror StepIndicator
  // and getActiveStepOrder() — key on `membershipFirst`.
  const membershipFirst =
    useMembershipPlanStore((s) => s.membershipFirst) &&
    marketHasMembership(market) &&
    (featureFlags?.["membership_v2"] ?? true);

  // Derived from the canonical step order (bookingFlowModel) so this bar and the
  // StepIndicator bar always agree. Map each step's key → the `header.*` namespace.
  const displaySteps = getIndicatorSteps(membershipFirst).map((s) => ({
    step: s.step,
    label: i18n(`header.${s.key}`),
  }));

  // Handle 'time' step, which is treated as 'date' for indicator purposes
  const stepToCheck = currentStep === "time" ? "date" : currentStep;
  const foundIndex = displaySteps.findIndex((s) => s.step === stepToCheck);
  const currentStepIndex = foundIndex === -1 ? 0 : foundIndex;
  const totalSteps = displaySteps.length;
  const currentStepData = displaySteps[currentStepIndex];

  // Don't show step indicator for confirmation step
  const shouldShowStepIndicator = currentStep && currentStep !== "confirmation";

  return (
    <>
      <header className="bg-gray-50">
        {/* Main header */}
        <div className="container mx-auto px-2 sm:px-4 py-5 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <button
              // The bare site root 307s to the Swiss homepage for everyone;
              // a German customer must land on the German site (AHD-8451).
              onClick={() =>
                (window.location.href = getHomepageUrl(market, language))
              }
              className="hover:opacity-80 transition-opacity cursor-pointer"
              aria-label="Go to homepage"
            >
              <img
                src={aheadLogo}
                alt="Ahead"
                className="max-h-8 w-auto max-w-24 sm:max-w-none"
              />
            </button>
          </div>

          {/* Support Button, Country and Language Selectors */}
          <div className="flex items-center gap-1 sm:gap-3">
            {!skeletonMode && <SupportButton language={language} />}
            <CountrySelector skeletonMode={skeletonMode} language={language} />
            {setLanguage && (
              <LanguageSelector
                skeletonMode={skeletonMode}
                language={language}
                setLanguage={setLanguage}
              />
            )}
          </div>
        </div>

        {/* Member recognition badge */}
        {!skeletonMode && <MemberHeaderBadge language={language} />}

        {/* Step Indicator */}
        {shouldShowStepIndicator && (
          <div className="container mx-auto px-4 pt-2 pb-3 lg:pb-5">
            {/* Conditionally render the step text or the skeleton loader */}
            <div className="text-center mb-[15px]">
              <span className="text-sm text-neutral-900">
                {!skeletonMode && (
                  <>
                    <span className="font-medium">{currentStepIndex + 1}</span>{" "}
                    {i18n("common.of")} {totalSteps}
                    <span className="mx-2">-</span>
                    <span className="font-medium">
                      {currentStepData ? currentStepData.label : ""}
                    </span>
                  </>
                )}
                {skeletonMode && (
                  <div className="h-4 w-48 bg-gray-200 rounded mx-auto mt-4 animate-pulse" />
                )}
              </span>
            </div>

            {/* Progress segments (DS ProgressBar) + per-step labels */}
            <ProgressBar
              currentStep={currentStepIndex}
              totalSteps={displaySteps.length}
            />
            <div className="flex gap-1.5 w-full pt-1">
              {displaySteps.map(({ label }, index) => (
                <div
                  key={index}
                  className={cn(
                    "flex-1 text-gray-300 text-xs hidden md:block text-center",
                    index === currentStepIndex && "text-black",
                    skeletonMode && "text-transparent",
                  )}
                >
                  {label}
                </div>
              ))}
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default BookingHeader;
